import React, { Suspense, useMemo } from 'react';
import { useGlobal } from 'reactn';
import { FolderNav, Loader, Theme } from '@orijinworks/frontend-commons';
import {
  LOCATION_MANAGEMENT_TAB_ICONS,
  SPLIT_MODULE_FOLDER_NAV_PROPS,
} from '../split-management-folder-nav';
import { isPermitted, PermissionControl } from '../../../components/permission-control/permission-control';
import { FacialAuth } from '../../../components/management';
import LocationDetailsModal from '../../../components/Locations/locationDetails';
import { useManagementSnackbar } from '../management-snackbar';
import { buildActiveLocation, useApplySelectedLocationAccess } from '../split-location-context';

const Settings = React.lazy(() => import('../../../components/management/settings'));
const Forms = React.lazy(() => import('../../../components/management/forms'));
const EditLocation = React.lazy(() => import('../../../components/manage-locations/edit/editLocation'));
const ManageHousingUnits = React.lazy(() => import('../../../components/manage-locations/edit/manageHousingUnits'));
const FacialAuthenticationSettings = React.lazy(() => import('../../../components/manage-locations/edit/facialAuthenticationSettings'));

const LocationManagement = ({ selectedLocation, selectedHousingUnit }) => {
  const [global, setGlobal] = useGlobal();
  const { showSnackBar, snackbar } = useManagementSnackbar();

  const location = useMemo(
    () => buildActiveLocation(selectedLocation, selectedHousingUnit),
    [selectedLocation, selectedHousingUnit]
  );
  useApplySelectedLocationAccess(location && location.active, global.cbacConfigFlag, setGlobal);

  const canAccessSettings =
    !global.isTeacher && isPermitted(global.cbacConfigFlag, global.permissions, 'Access_Location_Settings');
  const canViewForms =
    Boolean(location) &&
    location.type !== 'hu' &&
    isPermitted(global.cbacConfigFlag, global.permissions, 'View_Form_List');
  const isServicesAvailable = !global.isTeacher && Boolean(global.isSuperAdmin);

  const tabs = useMemo(() => {
    if (!location) {
      return [];
    }

    const locationState = { location };
    const nextTabs = [];

    if (canAccessSettings) {
      nextTabs.push({
        label: 'Settings',
        value: 'settings',
        Icon: LOCATION_MANAGEMENT_TAB_ICONS.settings,
        component: function SettingsTab() {
          return (
            <Suspense fallback={<Loader isLoading />}>
              <div style={{ padding: '1.5rem 70px', width: '100%' }}>
                <EditLocation selectedLocation={{ ...location, locationId: location.active }} />
                <PermissionControl permissionName="Access_Location_Settings">
                  <Settings showSnackBar={showSnackBar} facility {...locationState} generalSectionTitle="Access" />
                </PermissionControl>
                {global.isSuperAdmin && <FacialAuthenticationSettings locationId={location.active} />}
                {location.type !== 'hu' && (
                  <ManageHousingUnits
                    locationId={location.active}
                    locationName={location.locationName}
                    showSnackBar={showSnackBar}
                  />
                )}
              </div>
            </Suspense>
          );
        },
      });
    }

    if (canViewForms) {
      nextTabs.push({
        label: 'Forms',
        value: 'forms',
        Icon: LOCATION_MANAGEMENT_TAB_ICONS.forms,
        component: function FormsTab() {
          return (
            <Suspense fallback={<Loader isLoading />}>
              <PermissionControl permissionName="View_Form_List">
                <Forms showSnackBar={showSnackBar} {...locationState} />
              </PermissionControl>
            </Suspense>
          );
        },
      });
    }

    if (isServicesAvailable && location.type !== 'hu') {
      nextTabs.push({
        label: 'Services',
        value: 'services',
        Icon: LOCATION_MANAGEMENT_TAB_ICONS.services,
        component: function ServicesTab() {
          return <LocationDetailsModal inLine />;
        },
      });
    }

    if (global.isSuperAdmin) {
      nextTabs.push({
        label: 'Biometric Authentication',
        value: 'biometric-authentication',
        Icon: LOCATION_MANAGEMENT_TAB_ICONS['biometric-authentication'],
        component: function BiometricTab() {
          return <FacialAuth showSnackBar={showSnackBar} {...locationState} />;
        },
      });
    }

    return nextTabs;
  }, [canAccessSettings, canViewForms, global.isSuperAdmin, isServicesAvailable, location, showSnackBar]);

  if (!location || tabs.length === 0) {
    return null;
  }

  return (
    <div>
      {snackbar}
      <Theme>
        <FolderNav {...SPLIT_MODULE_FOLDER_NAV_PROPS} tabs={tabs} defaultActiveTab={tabs[0].value} />
      </Theme>
    </div>
  );
};

export default LocationManagement;
